// TODO(sierra-widget-removal): entire module unused after Shippy/Sierra split (only consumer was init-support-chat.js) — delete in follow-up PR, and confirm the enableSuncoAuthorization flag has no remaining consumers.
import axios from 'axios';
import { DateTime } from 'luxon';
import { defineMessage, defineMessages } from 'react-intl';
import Sunco from 'smooch'; // The underlying library for the widget hasn't been renamed to Sunco yet

import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import getIntl from '../../../intl/get-intl';
import { English, French, Spanish, German, ShipStationSupportedLocales } from '../../../intl/locale-context-provider/locale-codes';
import { LocalStorageService } from '../../../services/local-storage-service';
import { LogService } from '../../../services/log-service';
import { getPartitionNumber } from '../../../services/partition-service';

const isProduction = process.env.NODE_ENV === 'production';
const prod = '649b3ba288459a3184484a01';
const sandbox = '641dcf8a58926a010f9184fc';
const integrationId = isProduction ? prod : sandbox;

const brandColor = '1C272C';
const businessIconUrl = 'https://shipstation-cdn-us-east-1-813448775391.s3.amazonaws.com/images/shipstation-green-gear-200px.png';
const messagingOrigin = 'app';
const ssProductVersion = 'v3';

const enLocale = 'en-US';
const esLocale = 'es-US';
const frLocale = 'fr-FR';
const deLocale = 'de-DE';

const messages = defineMessages({
  errorNotificationTitle: {
    description: 'Error notification title for when signing into the chat widget via zendesk fails',
    defaultMessage: 'Unable to sign in to Zendesk',
    id: 'rXhKbf',
  },
  errorNotificationMessage: {
    description: 'Error notification message for when signing into the chat widget via zendesk fails',
    defaultMessage: 'We were unable to sign you in to Zendesk. Please try a different web browser.',
    id: 'x4nVzh',
  },
});

const getLocaleForSunco = (locale) => {
  const localeCodeMap = {
    en: enLocale,
    es: esLocale,
    fr: frLocale,
    de: deLocale,
  };
  return localeCodeMap[locale] || locale;
};

const getAppLanguage = (locale) => {
  const includedLocale = ShipStationSupportedLocales.find((loc) => loc === locale);
  const isValueCorrectLength = includedLocale && includedLocale.length > 2;

  if (!isValueCorrectLength) {
    if (includedLocale === English) {
      return enLocale;
    }
    if (includedLocale === French) {
      return frLocale;
    }
    if (includedLocale === Spanish) {
      return esLocale;
    }
    if (includedLocale === German) {
      return deLocale;
    }
  }
  return getLocaleForSunco(locale);
};

const webMessengerContainerSelector = 'web-messenger-container';
const headerSelector = '#header';
const wrapperSelector = '#wrapper';
const introPaneClass = '.intro-pane';
const closeButtonSelector = '.close-handle.brand-dark';
const conversationFooterSelector = '.conversation-group-footer';
const newConversationButtonSelector = '.conversation-group-footer button';
const inConversationFooterSelector = '#footer';
const headerTitleSelector = '.header-title';
const headerContentSelector = '.header-content';

const HIDDEN = 'hidden';
const VISIBLE = 'inherit';

export const getIframeElements = (selector) => {
  const iframe = document.getElementById(webMessengerContainerSelector);
  const iframedocument = iframe?.contentDocument || iframe?.contentWindow?.document;
  const elements = iframedocument?.querySelectorAll(selector);
  return elements;
};

// Function is run once on init, and will add the minimize button to the header bar
const addMinimizeButton = () => {
  const clickEvent = (event) => {
    event.stopPropagation();
    Sunco.close();
  };
  const header = getIframeElements(headerSelector)?.[0];
  if (!header) return;
  const minimizeButton = document.createElement('div');
  minimizeButton.style.fontSize = '20px';
  minimizeButton.style.paddingLeft = '11px';
  minimizeButton.style.alignItems = 'end';
  minimizeButton.style.paddingBottom = '10px';
  minimizeButton.className = 'close-handle brand-dark';
  minimizeButton.innerText = '_';
  minimizeButton.onclick = clickEvent;
  header.appendChild(minimizeButton);
};

class SuncoService {
  constructor() {
    this.userData = {};
    this.intl = null;
  }

  setWidgetVisibility(visibility) {
    const webMessengerContainer = document.getElementById(webMessengerContainerSelector);
    if (!webMessengerContainer) return;
    webMessengerContainer.style.visibility = visibility;
  }

  applyUniversalChatPositioning() {
    const webMessengerContainer = document.getElementById(webMessengerContainerSelector);
    if (!webMessengerContainer) return;
    webMessengerContainer.style.bottom = '45px';
  }

  styleChatWidget(iframe) {
    const headerTitle = iframe?.querySelector(headerTitleSelector);
    const wrapper = iframe?.querySelector(wrapperSelector);
    const newConversationButton = iframe?.querySelector(newConversationButtonSelector);
    const inConversationFooter = iframe?.querySelector(inConversationFooterSelector);
    if (headerTitle) headerTitle.style.color = '#ffffff';
    if (wrapper) wrapper.style.borderRadius = '2px';
    if (newConversationButton) newConversationButton.style.borderRadius = '2px';
    if (inConversationFooter) inConversationFooter.style.borderRadius = '2px';
  }

  /*  method is run whenever the widget is opened. Close button is rerendered each time open occurs.
    method will prevent event propagation on the header bar for click events. */
  modifyHeader() {
    const header = getIframeElements(headerSelector)?.[0];
    const buttons = getIframeElements(closeButtonSelector) || [];
    const closeButton = buttons?.[buttons.length - 1];
    if (!header || !closeButton) return;
    header.addEventListener('click', (event) => {
      const headerContent = getIframeElements(headerContentSelector)?.[0];
      const headerTitle = getIframeElements(headerTitleSelector)?.[0];
      if ([header, headerTitle, headerContent].some((element) => element === event.target)) {
        event.stopPropagation();
        event.preventDefault();
      }
    });
    header.style.cursor = 'default';
    closeButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      Sunco.close();
      this.setWidgetVisibility(HIDDEN);
    });
  }

  // This appends a legal disclaimer to the intro pane and could break if widget is updated, see SPD-14126 for details
  modifyIntroPane() {
    let introPane = getIframeElements(introPaneClass);
    const iframe = document.getElementById(webMessengerContainerSelector);
    const iframedocument = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!introPane.length) {
      return null;
    }
    if (!iframedocument.getElementById('legal-disclaimer')) {
      const messageDiv = document.createElement('div');
      messageDiv.setAttribute('id', 'legal-disclaimer');
      introPane = introPane[0];
      introPane.appendChild(messageDiv);
      introPane.style.fontSize = '12px';
      introPane.style.boxShadow = '2px 2px 8px #e3e8e5';
      introPane.style.zIndex = '1';

      const legalDisclaimerText = this.getLegalDisclaimerText();
      messageDiv.innerText = legalDisclaimerText;
    }
  }

  // TODO SPD-14126: See ticket for details, this replaces the widget's button with a button that calls loadConversation
  replaceNewConversationButton(iframe) {
    const conversationFooter = iframe.querySelector(conversationFooterSelector);
    if (conversationFooter) {
      Array.from(conversationFooter.children).forEach((child) => {
        conversationFooter.removeChild(child);
      });
      const button = document.createElement('button');
      const message = defineMessage({
        description: 'text for button to start a new conversation in sunco chat widget',
        defaultMessage: 'New Conversation',
        id: 'KLJ0Av',
      });
      button.innerText = this.intl?.formatMessage(message) ?? message.defaultMessage;
      button.style.borderRadius = '2px';
      button.onclick = this.loadConversation;
      conversationFooter.appendChild(button);
    }
  }

  bindSuncoEvents() {
    Sunco.on('widget:opened', () => {
      if (Sunco.getConversations().length === 0) {
        Sunco.createConversation();
      }
      const iframe = document.getElementById(webMessengerContainerSelector);
      const iframedocument = iframe?.contentDocument || iframe?.contentWindow?.document;
      this.styleChatWidget(iframedocument);
      this.replaceNewConversationButton(iframedocument);
      this.applyUniversalChatPositioning();
      iframedocument.addEventListener('click', () => {
        this.replaceNewConversationButton(iframedocument);
      });

      this.modifyHeader();
      this.modifyIntroPane();

      iframedocument.addEventListener(
        'click',
        () => {
          /*  Use setTimeout to ensure that the current conversation is loaded after click
            Otherwise, the event triggers during the animation and the conversation id is not loaded  */
          setTimeout(() => {
            const currentConversation = Sunco.getDisplayedConversation();
            if (currentConversation) {
              this.modifyIntroPane();
            }
          }, 500);
        },
        false,
      );
    });

    Sunco.on('unreadCount', () => {
      if (!Sunco.isOpened()) {
        this.setWidgetVisibility(VISIBLE);
        this.applyUniversalChatPositioning();
      }
    });
  }

  // Most methods on the Sunco object are undefined until Sunco.init() is called.
  hasSuncoBeenInitialized() {
    return !!Sunco.getUser;
  }

  getZendeskJwt = async () => axios.get('/auth/sunco-signin').then(({ data: jwt }) => jwt);

  getChatAuthData = async ({ email, sellerId, enableSuncoChatAuth }) => {
    if (!enableSuncoChatAuth) {
      return {};
    }
    // The web widget sets three values to localstorage to save chat history for unauthenticated users
    // They are prefixed with the integrationId
    LocalStorageService.clearItemsWithPrefix(integrationId);
    const chatAuthData = await this.getZendeskJwt()
      .then((jwt) => {
        return { jwt, externalId: `shipstation-${sellerId}-${email}` };
      })
      .catch((error) => {
        LogService.logError(error);
        return {};
      });

    return chatAuthData;
  };

  getLocalizedBusinessName = () => {
    const message = defineMessage({
      description: 'Company Name',
      defaultMessage: 'ShipStation',
      id: 'PE0d/Z',
    });

    return this.intl?.formatMessage(message) ?? message.defaultMessage;
  };

  getLegalDisclaimerText = () => {
    const message = defineMessage({
      description: 'Legal Disclaimer Text',
      defaultMessage:
        'By starting a chat with our artificial intelligence-powered assistant, you agree to the automated processing of your personal data.',
      id: 'U7xmS2',
    });

    return this.intl?.formatMessage(message) ?? message.defaultMessage;
  };

  setIntl = async (locale) => {
    this.intl = await getIntl(locale);
  };

  loadConversation() {
    if (Sunco.isOpened()) {
      Sunco.createConversation({
        metadata: this.userData,
      }).then((conversation) => {
        Sunco.loadConversation(conversation.id);
      });
    }
  }

  updateUserMetadata({
    activationDate,
    email,
    homeCountry,
    locale,
    name,
    sellerId,
    subscriptionPlan,
    userHasAdmin,
    billingCycleDate,
    businessName,
  }) {
    if (this.hasSuncoBeenInitialized()) {
      const formattedBillingDate = billingCycleDate ? DateTime.fromISO(billingCycleDate).toFormat('yyyy/MM/dd') : null;
      const formattedStartDate = DateTime.fromISO(activationDate).toFormat('yyyy/MM/dd');
      const formattedAppLanguage = getAppLanguage(locale).toLowerCase();

      this.userData = {
        accountCountry: homeCountry.toLowerCase(),
        appLanguage: formattedAppLanguage,
        billingCycleDate: formattedBillingDate,
        brand: businessName?.defaultMessage?.toLowerCase() || businessName?.toLowerCase(),
        messagingOrigin,
        planLevel: subscriptionPlan.planName?.replace(/\s/g, '').toLowerCase(),
        sellerId,
        ssEnvironment: `ss${getPartitionNumber()}`,
        ssProductVersion,
        startDate: formattedStartDate,
        userAdmin: userHasAdmin,
      };

      Sunco.updateUser({
        givenName: name,
        email,
        metadata: this.userData,
      });
    }
  }

  async initSuncoChat({
    name,
    email,
    sellerId,
    homeCountry,
    subscriptionPlan,
    activationDate,
    locale,
    userHasAdmin,
    billingCycleDate,
    enableSuncoChatAuth,
  }) {
    this.bindSuncoEvents();
    if (!this.hasSuncoBeenInitialized()) {
      await this.setIntl(locale);
      const businessName = this.getLocalizedBusinessName();
      const chatAuthData = await this.getChatAuthData({
        email,
        sellerId,
        enableSuncoChatAuth,
      });

      return Sunco.init({
        integrationId,
        locale: getLocaleForSunco(locale),
        businessName,
        businessIconUrl,
        customColors: {
          brandColor,
          conversationColor: brandColor,
          actionColor: brandColor,
        },
        menuItems: {
          imageUpload: true,
          fileUpload: true,
        },
        ...chatAuthData,
      })
        .then(() => {
          this.updateUserMetadata({
            name,
            email,
            sellerId,
            homeCountry,
            subscriptionPlan,
            activationDate,
            locale,
            userHasAdmin,
            billingCycleDate,
            businessName,
          });
          addMinimizeButton();
          Sunco.setDelegate({
            onInvalidAuth: this.getZendeskJwt,
          });
          Sunco.open();
          this.applyUniversalChatPositioning();
        })
        .catch((error) => {
          LogService.logError(error);
          NotificationService.showNotification({
            notificationType: NOTIFICATION_TYPE_ERROR,
            title: this.intl.formatMessage(messages.errorNotificationTitle),
            content: this.intl.formatMessage(messages.errorNotificationMessage),
          });
        });
    }
    this.setWidgetVisibility(VISIBLE);
    Sunco.open();
    this.applyUniversalChatPositioning();
  }
}

export default new SuncoService();
